// src/components/CoverSlider.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import MediaRenderer from "./MediaRenderer";
import MusicEmbedModal from "./MusicEmbedModal";
import { getSpotifyEmbed, normalizeLink } from "../lib/musicEmbeds";
import { mergeSiteContent } from "../utils/helpers";
import { typographyFor } from "../lib/typography";

export default function CoverSlider() {
  const navigate = useNavigate();
  const [covers, setCovers] = useState([]);
  const [coverLinks, setCoverLinks] = useState([]);
  const [title, setTitle] = useState("Découvrez nos dernières pochettes");
  const [site, setSite] = useState(() => mergeSiteContent());
  const [current, setCurrent] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [musicPlayer, setMusicPlayer] = useState(null);
  const hoverRef = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 640);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Firestore temps réel
  useEffect(() => {
    let unsub;
    (async () => {
      const { db } = await import("../firebase");
      const ref = doc(db, "admin", "content");
      unsub = onSnapshot(ref, (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const site = mergeSiteContent(data);
        const links = Array.isArray(data.coverLinks) ? data.coverLinks : [];
        const entries = Array.isArray(data.covers)
          ? data.covers.map((src, index) => ({ src, link: links[index] || "" })).filter((item) => item.src)
          : [];
        setCovers(entries.map((item) => item.src));
        setCoverLinks(entries.map((item) => item.link));
        setSite(site);
        setTitle(site.pages.home.coversTitle);
        setCurrent(0);
      });
    })();
    return () => unsub && unsub();
  }, []);

  // Autoplay (2.5s), pause au survol
  useEffect(() => {
    if (!covers.length) return;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!hoverRef.current) {
        setCurrent((i) => (i + 1) % covers.length);
      }
    }, 2000);
    return () => clearInterval(intervalRef.current);
  }, [covers.length]);

  // fenêtre de 5 autour du centre : [-2, -1, 0, +1, +2]
  const visible = useMemo(() => {
    const n = covers.length;
    if (!n) return [];
    const offsets = isMobile
      ? n === 1 ? [0] : [-1, 0, 1]
      : n === 1 ? [0] : n === 2 ? [-1, 0] : n === 3 ? [-1, 0, 1] : [-2, -1, 0, 1, 2];
    return offsets.map((k) => {
      const idx = ((current + k) % n + n) % n;
      return {
        k,
        idx,
        url: covers[idx],
        link: normalizeLink(coverLinks[idx]),
      };
    });
  }, [covers, coverLinks, current, isMobile]);

  if (!covers.length) {
    return (
      <section className="bg-black text-white px-6 py-16">
        <h3 className="text-xl text-center text-gray-400">Covers en preparation</h3>
      </section>
    );
  }

  // Définir tailles selon distance
  const sizeForK = (k) => {
    const d = Math.abs(k);
    if (isMobile) {
      if (d === 0) return 250;
      if (d === 1) return 135;
      return 100;
    }
    if (d === 0) return 320;
    if (d === 1) return 200;
    return 160;
  };

  const styleForK = (k) => {
    const w = sizeForK(k);
    const d = Math.abs(k);
    const opacity = d === 0 ? 1 : d === 1 ? 0.9 : 0.7;
    const z = d === 0 ? 40 : d === 1 ? 25 : 10;

    return {
      position: "absolute",
      left: "50%",
      top: "50%",
      width: `${w}px`,
      height: `${w}px`,
      transform: `translate(-50%, -50%) translateX(${k * (isMobile ? 120 : 220)}px)`,
      transition:
        "transform 500ms ease, opacity 500ms ease, width 500ms ease, height 500ms ease",
      opacity,
      zIndex: z,
      borderRadius: "20px",
      overflow: "hidden",
      boxShadow:
        d === 0
          ? "0 16px 40px rgba(0,0,0,0.6)"
          : "0 8px 24px rgba(0,0,0,0.35)",
      backgroundColor: "rgb(24 24 27)",
      cursor: d === 0 ? "pointer" : "default", // curseur main seulement au centre
    };
  };

  return (
    <section
      className="relative bg-black text-white py-16"
      onMouseEnter={() => (hoverRef.current = true)}
      onMouseLeave={() => (hoverRef.current = false)}
    >
      <h2 className="text-2xl md:text-3xl font-bold px-6 md:px-12 mb-8" style={typographyFor(site, "homeCoversTitle")}>
        {title}
      </h2>

      <div className="relative mx-auto" style={{ height: isMobile ? 300 : 360 }}>
        {/* dégradés latéraux */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-black to-transparent z-20" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-black to-transparent z-20" />

        {/* zone d'affichage */}
        <div className="relative overflow-hidden" style={{ height: isMobile ? 300 : 360 }}>
          {visible.map(({ k, idx, url, link }) => {
            const isActive = idx === current;
            const spotify = getSpotifyEmbed(link);
            return (
              <button
                type="button"
                key={`${idx}-${url}`}
                onClick={() => {
                  if (!isActive) {
                    setCurrent(idx); // premier clic → mettre devant
                  } else if (link) {
                    if (spotify) {
                      setMusicPlayer({
                        ...spotify,
                        title: `${spotify.label} Unlimited Session`,
                      });
                    } else if (link.startsWith("/")) {
                      navigate(link);
                    } else {
                      window.open(link, "_blank", "noopener,noreferrer");
                    }
                  }
                }}
                aria-label={spotify ? `Écouter la cover ${idx + 1}` : `Cover ${idx + 1}`}
                style={styleForK(k)}
                className="group rounded-2xl overflow-hidden"
              >
                <MediaRenderer
                  src={url}
                  type="auto"
                  alt={`Cover ${idx + 1}`}
                  background
                  className="w-full h-full object-cover"
                  loading="lazy"
                  draggable={false}
                />
                {isActive && link && (
                  <span className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-white/20 bg-black/65 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-white opacity-0 backdrop-blur transition group-hover:opacity-100">
                    {spotify ? "Écouter ici" : "Ouvrir"}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* flèches */}
        <div className="absolute inset-y-0 left-4 flex items-center z-30">
          <button
            type="button"
            className="inline-flex bg-white/10 hover:bg-white/20 rounded-full p-2 backdrop-blur"
            onClick={() => setCurrent((i) => (i - 1 + covers.length) % covers.length)}
            aria-label="Précédent"
          >
            ‹
          </button>
        </div>
        <div className="absolute inset-y-0 right-4 flex items-center z-30">
          <button
            type="button"
            className="inline-flex bg-white/10 hover:bg-white/20 rounded-full p-2 backdrop-blur"
            onClick={() => setCurrent((i) => (i + 1) % covers.length)}
            aria-label="Suivant"
          >
            ›
          </button>
        </div>
      </div>
      <MusicEmbedModal
        player={musicPlayer}
        title={musicPlayer?.title || "Unlimited Session"}
        onClose={() => setMusicPlayer(null)}
      />
    </section>
  );
}
